/**
 * Presentational sections for `/admin/automation/workloads` (A7Y-3475) — the
 * Actions routing audit + per-workflow cost board. Every component here is
 * pure: `WorkloadsPage` owns the fetch, this file only renders what it is
 * given.
 *
 * Two conventions this file borrows deliberately rather than reinventing:
 *   - the light + `BannerRule` pattern from `AddisStatusBoard.tsx` (same
 *     directory). That file's own comment explains why it doesn't reuse
 *     `platform-health/Stoplight`: the reason needs to sit on its own line
 *     below the header rather than welded inline. This board has the same
 *     shape (a composed sentence, not a fixed reason string — see below) so
 *     it gets its own small badge rather than importing an unexported one.
 *   - the CVD-validated colour triad from `CdidFlowSection.tsx`
 *     (`audit-spend/DailyTrendChart.tsx`'s palette) for the per-workflow
 *     trend chart, rather than a fourth copy of the same three hues.
 *
 * `verdict` on the wire is a bare `'red' | 'amber' | 'green'` — there is no
 * server-authored reason string (unlike `AddisSystemStatus.reason`). This
 * file COMPOSES the banner sentence from `findings` + `routing` + `stale` so
 * the light is never shown without an answer to "why". Dates: `weeks[]`
 * entries and `sinceAtLeast` are ISO calendar dates (Monday-anchored week
 * keys / day-granular finding ages), not instants — they render as plain
 * text or through a UTC-anchored formatter, never through `fmtLocal`, which
 * would shift a calendar date by a day for negative-offset viewers.
 * `producedAt` / `generatedAt` are instants and do get `fmtLocal`.
 */
import { useId, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  WorkloadFailoverState,
  WorkloadFinding,
  WorkloadPoolEvidence,
  WorkloadRoutingRow,
  WorkloadRoutingStatus,
  WorkloadsStatus,
  WorkloadWorkflowSummary,
} from '@a7y/types';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/useTheme';
import { fmtLocal, fmtUtc } from './shared';
import { composeVerdictSentence } from './workloads-verdict';

// ---------------------------------------------------------------------------
// MARK: verdict banner
// ---------------------------------------------------------------------------

type Verdict = WorkloadsStatus['verdict'];

const LIGHT_PRESENTATION: Record<Verdict, { glyph: string; label: string; className: string }> = {
  green: { glyph: '🟢', label: 'Green', className: 'text-emerald-700 dark:text-emerald-300' },
  amber: { glyph: '🟡', label: 'Amber', className: 'text-amber-700 dark:text-amber-300' },
  // Never rendered in a reassuring colour — red always gets the red classes,
  // never folded into a neutral or muted treatment.
  red: { glyph: '🔴', label: 'Red', className: 'text-red-700 dark:text-red-300' },
};

/** A horizontal rule with a label riding on it — matches `AddisStatusBoard`'s `BannerRule`. */
export function BannerRule({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="h-px w-6 shrink-0 bg-zinc-300 dark:bg-zinc-700" />
      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 whitespace-nowrap">
        {children}
      </span>
      <span className="h-px flex-1 bg-zinc-300 dark:bg-zinc-700" />
    </div>
  );
}

function WorkloadsLightBadge({ verdict, sentence }: { verdict: Verdict; sentence: string }) {
  const { glyph, label, className } = LIGHT_PRESENTATION[verdict];
  return (
    <span
      role="status"
      aria-label={`${label} — ${sentence}`}
      className={cn('inline-flex items-center gap-1.5 text-sm font-semibold', className)}
    >
      <span aria-hidden="true">{glyph}</span>
      {label}
    </span>
  );
}

export function WorkloadsVerdictBanner({ status }: { status: WorkloadsStatus }) {
  const sentence = composeVerdictSentence(status);
  return (
    <section className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 space-y-1.5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Actions workload &amp; routing audit
        </h2>
        <WorkloadsLightBadge verdict={status.verdict} sentence={sentence} />
      </div>
      <p className="text-xs text-zinc-600 dark:text-zinc-400">{sentence}</p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// MARK: freshness chip
// ---------------------------------------------------------------------------

export function FreshnessChip({
  producedAt,
  stale,
  staleAfterHours,
}: {
  producedAt: string | null;
  stale: boolean;
  staleAfterHours: number;
}) {
  const toneClass = stale
    ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
    : 'border-zinc-200 bg-zinc-50 text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
        toneClass
      )}
      // "Producer has not run yet" and "produced 0 hours ago" are different
      // facts and must never share a rendering.
      title={producedAt ? `Produced ${fmtUtc(producedAt)} · stale after ${staleAfterHours}h` : undefined}
    >
      {producedAt ? (
        <>Produced {fmtLocal(producedAt)}</>
      ) : (
        <>Producer has not run yet</>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// MARK: routing table
// ---------------------------------------------------------------------------

const STATUS_META: Record<WorkloadRoutingStatus, { label: string; className: string; note: string }> = {
  'on-pool': {
    label: 'On pool',
    className: 'text-emerald-700 dark:text-emerald-300',
    note: 'Running on UKKO as expected.',
  },
  'off-pool-overflow': {
    label: 'Overflow',
    className: 'text-amber-700 dark:text-amber-300',
    note: 'Overflow routing engaged for this class.',
  },
  'off-pool-failover': {
    label: 'Failover',
    className: 'text-amber-700 dark:text-amber-300',
    note: 'General health failover in effect.',
  },
  'off-pool-disabled': {
    label: 'Disabled',
    className: 'text-amber-700 dark:text-amber-300',
    note: 'An operator took this class off-pool.',
  },
  'off-pool-exile': {
    label: 'Exile',
    // Never a reassuring colour — this is the stuck signal.
    className: 'text-red-700 dark:text-red-300 font-semibold',
    note: 'Off-pool for no recorded reason.',
  },
  'absent-fail-safe': {
    label: 'Not cut over',
    className: 'text-zinc-500 dark:text-zinc-400',
    note: 'Not yet cut over — expected to be unset.',
  },
  'absent-unexpected': {
    label: 'Missing',
    className: 'text-amber-700 dark:text-amber-300',
    note: 'Missing, but doctrine expects this class to be set.',
  },
  unreadable: {
    label: 'Unknown',
    className: 'text-zinc-500 dark:text-zinc-400 italic',
    note: 'Live value could not be read this cycle.',
  },
};

/** Doctrine's currently-intended value: the UKKO target once cut over, the
 *  GitHub-hosted fallback while cutover hasn't landed. Independent of the
 *  row's live status — this is what doctrine WANTS, not what's happening. */
function intendedValue(row: WorkloadRoutingRow): string {
  return row.cutover ? row.ukkoValue : row.githubValue;
}

/**
 * The restore command, rendered as inert text with a click-to-copy
 * affordance — deliberately NOT a `<button>` / `role="button"`. This page is
 * read-only by construction (plan exclusion #2: no remediation from the UI);
 * routing is shared infrastructure a console click must never mutate.
 */
function RestoreCommand({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);
  const describedById = useId();
  const handleCopy = () => {
    navigator.clipboard?.writeText(command).catch(() => {});
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="mt-1 flex items-center gap-2">
      {/* Deliberately `<code>`, not `<button>`/`role="button"`: this page is
       *  read-only by construction (no remediation from the UI) and routing
       *  is shared infrastructure a console click must never mutate. The
       *  click-to-copy affordance only copies inert text to the clipboard —
       *  it does not act on the infrastructure the command names — so a
       *  visually-hidden description (rather than an action-implying role)
       *  is what a keyboard/AT user needs on focus. Do not "fix" this into
       *  a button. */}
      <code
        tabIndex={0}
        onClick={handleCopy}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCopy();
          }
        }}
        title="Click to copy — text only, this page never executes it"
        aria-describedby={describedById}
        className="cursor-pointer select-all rounded bg-zinc-100 px-2 py-1 font-mono text-[11px] text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
      >
        {command}
      </code>
      <span id={describedById} className="sr-only">
        Copy this restore command to the clipboard. It is not executed.
      </span>
      <span aria-live="polite" className="text-[10px] text-zinc-400">
        {copied ? 'Copied' : ''}
      </span>
    </div>
  );
}

function failoverStateLine(state: WorkloadFailoverState | null): string {
  if (!state) return 'Failover state: unknown.';
  const overflow = state.overflowActive ? 'active' : 'inactive';
  const failedOverAt = state.failedOverAt ? fmtLocal(state.failedOverAt) : 'never';
  const disabled = state.disabledClasses.length > 0 ? state.disabledClasses.join(', ') : 'none';
  return `Overflow: ${overflow} · Failed over: ${failedOverAt} · Disabled classes: ${disabled}`;
}

export function RoutingTable({
  routing,
  failoverState,
}: {
  routing: WorkloadRoutingRow[];
  failoverState: WorkloadFailoverState | null;
}) {
  return (
    <section className="space-y-2 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
      <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Routing</h2>
      <div className="overflow-x-auto">
        <table className="min-w-[48rem] w-full text-sm">
          <thead className="bg-zinc-50 dark:bg-zinc-900 text-left text-xs text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Class</th>
              <th className="px-3 py-2 font-medium">Intended</th>
              <th className="px-3 py-2 font-medium">Live value</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {routing.map((row) => {
              const meta = STATUS_META[row.status];
              return (
                <tr key={row.class}>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-700 dark:text-zinc-300">
                    {row.class}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-600 dark:text-zinc-400">
                    {intendedValue(row)}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-700 dark:text-zinc-300">
                    {row.liveValue ?? '(absent)'}
                  </td>
                  <td className={cn('px-3 py-2 text-xs font-medium', meta.className)}>{meta.label}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500 dark:text-zinc-400">
                    {meta.note}
                    {row.restoreCommand && <RestoreCommand command={row.restoreCommand} />}
                  </td>
                </tr>
              );
            })}
            {routing.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-xs text-zinc-400">
                  No routing classes reported.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-zinc-400">{failoverStateLine(failoverState)}</p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// MARK: findings table
// ---------------------------------------------------------------------------

const FINDING_KIND_LABELS: Record<WorkloadFinding['kind'], string> = {
  EXILE: 'Exile',
  DOCTRINE_VIOLATION: 'Doctrine violation',
  SPLIT_PLACEMENT: 'Split placement',
  VERIFY: 'Verify',
};

function fmtFindingCost(v: number | null): string {
  return v == null ? '—' : `$${Math.round(v).toLocaleString('en-US')}`;
}

export function FindingsTable({ findings }: { findings: WorkloadFinding[] }) {
  if (findings.length === 0) {
    return (
      <section className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Findings</h2>
        <p className="mt-2 text-xs text-zinc-500">No findings.</p>
      </section>
    );
  }

  return (
    <section className="space-y-2 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
      <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Findings</h2>
      {/* Rows arrive most-costly-first from the server — rendered in that
          order verbatim, never re-sorted here. */}
      <div className="overflow-x-auto">
        <table className="min-w-[56rem] w-full text-sm">
          <thead className="bg-zinc-50 dark:bg-zinc-900 text-left text-xs text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Finding</th>
              <th className="px-3 py-2 font-medium">Workflow</th>
              <th className="px-3 py-2 font-medium">Expected</th>
              <th className="px-3 py-2 font-medium">Actual</th>
              <th className="px-3 py-2 font-medium">Since</th>
              <th className="px-3 py-2 font-medium text-right">Est. $/mo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {findings.map((finding, index) => (
              <tr key={`${finding.kind}-${finding.workflow ?? finding.class ?? index}-${index}`}>
                <td className="px-3 py-2 text-xs font-medium text-zinc-800 dark:text-zinc-200">
                  {FINDING_KIND_LABELS[finding.kind]}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-zinc-600 dark:text-zinc-400">
                  {finding.workflow ?? finding.class ?? '—'}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-zinc-600 dark:text-zinc-400">
                  {finding.expected ?? '—'}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-zinc-600 dark:text-zinc-400">
                  {finding.actual ?? '—'}
                </td>
                <td className="px-3 py-2 text-xs text-zinc-500 dark:text-zinc-400">
                  {/* Calendar date, not an instant — rendered verbatim, never
                      through fmtLocal (which would shift it by a day). */}
                  {finding.sinceAtLeast ? `≥ ${finding.sinceAtLeast}` : '—'}
                </td>
                <td className="px-3 py-2 text-right text-xs font-medium tabular-nums text-zinc-800 dark:text-zinc-200">
                  {fmtFindingCost(finding.estMonthlyUsd)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// MARK: per-workflow summary table
// ---------------------------------------------------------------------------

/** `YYYY-MM-DD` (a Monday, per the engine's `mondayOf`) → `2 Aug`. UTC on
 *  purpose — this is a calendar date, not an instant; `fmtLocal` would shift
 *  it a day for a negative-offset viewer. Mirrors `CdidFlowSection`'s
 *  `formatDayShort` rather than importing it, since that file is a sibling
 *  component's private helper, not a shared export. */
function formatWeekShort(week: string): string {
  const d = new Date(`${week}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return week;
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short' }).format(d);
}

function fmtPct(v: number | null): string {
  return v == null ? '—' : `${v.toFixed(1)}%`;
}

function fmtUsd(v: number): string {
  return v > 0 ? `$${v.toFixed(2)}` : '—';
}

/** Same validated triad `CdidFlowSection` reuses from `audit-spend/DailyTrendChart.tsx`. */
const TREND_COLORS = {
  light: { runs: '#f59e0b', ghCost: '#3b82f6' },
  dark: { runs: '#c98500', ghCost: '#3987e5' },
} as const;

function WeeklyTrendChart({
  weeks,
  workflow,
  gridStroke,
  colors,
}: {
  weeks: string[];
  workflow: WorkloadWorkflowSummary;
  gridStroke: string;
  colors: { runs: string; ghCost: string };
}) {
  const data = weeks.map((week) => ({
    week,
    runs: workflow.weekly[week]?.runs ?? 0,
    ghCost: workflow.weekly[week]?.ghCost ?? 0,
  }));
  const axisTick = { fontSize: 10, fill: '#9ca3af' };

  return (
    <div className="h-[140px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
          <XAxis
            dataKey="week"
            tick={axisTick}
            axisLine={{ stroke: gridStroke, strokeWidth: 1 }}
            tickLine={false}
            dy={4}
            minTickGap={12}
            tickFormatter={formatWeekShort}
          />
          <YAxis yAxisId="runs" width={28} tick={axisTick} axisLine={false} tickLine={false} />
          <YAxis
            yAxisId="cost"
            orientation="right"
            width={40}
            tick={axisTick}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            formatter={(value: number, name: string) =>
              name === 'GH spend' ? [fmtUsd(value), name] : [value, name]
            }
            labelFormatter={(label: string) => formatWeekShort(label)}
          />
          <Bar
            yAxisId="runs"
            dataKey="runs"
            name="Runs"
            fill={colors.runs}
            radius={[2, 2, 0, 0]}
            maxBarSize={16}
            isAnimationActive={false}
          />
          <Line
            yAxisId="cost"
            type="monotone"
            dataKey="ghCost"
            name="GH spend"
            stroke={colors.ghCost}
            strokeWidth={2}
            dot={{ r: 2, strokeWidth: 0, fill: colors.ghCost }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function WeeklyCellsTable({ weeks, workflow }: { weeks: string[]; workflow: WorkloadWorkflowSummary }) {
  return (
    <table className="min-w-[42rem] w-full text-xs">
      <thead className="text-left text-zinc-500">
        <tr>
          <th className="px-2 py-1 font-medium">Week (Mon)</th>
          <th className="px-2 py-1 font-medium text-right">Runs</th>
          <th className="px-2 py-1 font-medium text-right">GH</th>
          <th className="px-2 py-1 font-medium text-right">UKKO</th>
          <th className="px-2 py-1 font-medium text-right">GH ok</th>
          <th className="px-2 py-1 font-medium text-right">UKKO ok</th>
          <th className="px-2 py-1 font-medium text-right">Run ok</th>
          <th className="px-2 py-1 font-medium text-right">GH spend</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
        {weeks.map((week) => {
          const cells = workflow.weekly[week];
          return (
            <tr key={week}>
              <td className="px-2 py-1 font-mono text-zinc-600 dark:text-zinc-400">{week}</td>
              {cells ? (
                <>
                  <td className="px-2 py-1 text-right tabular-nums">{cells.runs}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{cells.ghRuns}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{cells.ukkoRuns}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmtPct(cells.ghOk)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmtPct(cells.ukkoOk)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmtPct(cells.runOk)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmtUsd(cells.ghCost)}</td>
                </>
              ) : (
                <td colSpan={7} className="px-2 py-1 text-center text-zinc-400">
                  no runs
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function WorkflowRow({
  workflow,
  weeks,
  gridStroke,
  trendColors,
}: {
  workflow: WorkloadWorkflowSummary;
  weeks: string[];
  gridStroke: string;
  trendColors: { runs: string; ghCost: string };
}) {
  const [expanded, setExpanded] = useState(false);
  const s = workflow.summary;
  return (
    <>
      <tr>
        <td className="px-3 py-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="inline-flex items-center gap-1.5 font-mono text-xs text-zinc-700 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100"
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            )}
            {workflow.name}
          </button>
        </td>
        <td className="px-3 py-2 text-right text-xs tabular-nums">{s.runs}</td>
        <td className="px-3 py-2 text-right text-xs tabular-nums">{s.ghRuns}</td>
        <td className="px-3 py-2 text-right text-xs tabular-nums">{s.ukkoRuns}</td>
        <td className="px-3 py-2 text-right text-xs tabular-nums">{fmtPct(s.ghOk)}</td>
        <td className="px-3 py-2 text-right text-xs tabular-nums">{fmtPct(s.ukkoOk)}</td>
        <td className="px-3 py-2 text-right text-xs tabular-nums">{fmtPct(s.runOk)}</td>
        <td className="px-3 py-2 text-right text-xs tabular-nums">{fmtUsd(s.ghCost)}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} className="bg-zinc-50 px-3 py-3 dark:bg-zinc-950/40">
            <div className="space-y-3">
              <WeeklyTrendChart weeks={weeks} workflow={workflow} gridStroke={gridStroke} colors={trendColors} />
              <div className="overflow-x-auto">
                <WeeklyCellsTable weeks={weeks} workflow={workflow} />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function WorkflowSummaryTable({
  workflows,
  weeks,
}: {
  workflows: WorkloadWorkflowSummary[];
  weeks: string[];
}) {
  const { resolvedTheme } = useTheme();
  const trendColors = TREND_COLORS[resolvedTheme === 'dark' ? 'dark' : 'light'];
  const gridStroke = resolvedTheme === 'dark' ? '#3f3f46' : '#e4e4e7';

  return (
    <section className="space-y-2 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
      <div>
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Per-workflow (7d)</h2>
        <p className="mt-0.5 text-xs text-zinc-500">Expand a row for its weekly breakdown and trend.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-[52rem] w-full text-sm">
          <thead className="bg-zinc-50 dark:bg-zinc-900 text-left text-xs text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Workflow</th>
              <th className="px-3 py-2 font-medium text-right">Runs</th>
              <th className="px-3 py-2 font-medium text-right">GH</th>
              <th className="px-3 py-2 font-medium text-right">UKKO</th>
              <th className="px-3 py-2 font-medium text-right">GH ok</th>
              <th className="px-3 py-2 font-medium text-right">UKKO ok</th>
              <th className="px-3 py-2 font-medium text-right">Run ok</th>
              <th className="px-3 py-2 font-medium text-right">GH spend</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {workflows.map((workflow) => (
              <WorkflowRow
                key={workflow.path ?? workflow.name}
                workflow={workflow}
                weeks={weeks}
                gridStroke={gridStroke}
                trendColors={trendColors}
              />
            ))}
            {workflows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-center text-xs text-zinc-400">
                  No workflow data reported.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// MARK: legend + unreadable note
// ---------------------------------------------------------------------------

export function WorkloadsLegend({ poolEvidence }: { poolEvidence: WorkloadPoolEvidence | null }) {
  return (
    <section className="space-y-1.5 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 text-[11px] text-zinc-500 dark:text-zinc-400">
      <p>
        <span className="font-semibold text-zinc-700 dark:text-zinc-300">Exact:</span> run counts and
        run success rate (the run's own recorded conclusion, one API read per workflow-week).
      </p>
      <p>
        <span className="font-semibold text-zinc-700 dark:text-zinc-300">Sampled and scaled:</span>{' '}
        the GH/UKKO plane split, GH ok%, UKKO ok%, and GH spend — derived from a bounded per-job
        sample, spread across the bucket and scaled by the exact run count. Trust the ranking and the
        week-over-week shape over the absolute figure.
      </p>
      <p>
        <span className="font-semibold text-zinc-700 dark:text-zinc-300">Pool evidence window:</span>{' '}
        {poolEvidence
          ? `a class only shows a corroborated exile finding when UKKO landed at least one job in the trailing ${poolEvidence.windowHours}h.`
          : 'not reported this cycle — exile findings could not be corroborated against pool health.'}
      </p>
    </section>
  );
}

export function UnreadableNote({ unreadable }: { unreadable: string[] }) {
  if (unreadable.length === 0) return null;
  return (
    <p className="text-xs text-amber-700 dark:text-amber-400">Could not read: {unreadable.join(', ')}</p>
  );
}
